import { useEffect, useMemo, useState } from 'react';
import { ImageIcon, Loader2, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PaginationBar } from '@/components/data-grid';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/app/routes';
import type { PostResponseDTO } from '@/api/generated/model';
import { useUserTopContent } from '../api';
import { formatCardDate, postThumbnailSrc } from '../lib/media';
import { BulkUserContentActions } from './BulkUserContentActions';

/** True when the post is hidden or terminated. */
function isHidden(post: PostResponseDTO): boolean {
  const s = (post.postStatus ?? '').trim().toUpperCase();
  return s === 'HID' || s === 'HIDDEN' || s === 'TER' || s === 'TERMINATED';
}

/** True when the post is on pre-publish hold (WAT). */
function isWaiting(post: PostResponseDTO): boolean {
  const s = (post.postStatus ?? '').trim().toUpperCase();
  return s === 'WAT' || s === 'WAITING';
}

/** True when the post is permanently terminated (not selectable for bulk actions). */
function isTerminated(post: PostResponseDTO): boolean {
  const s = (post.postStatus ?? '').trim().toUpperCase();
  return s === 'TER' || s === 'TERMINATED';
}

function AllContentCard({
  post,
  onOpen,
  selectable,
  selected,
  onToggleSelect,
}: {
  post: PostResponseDTO;
  onOpen: () => void;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const src = postThumbnailSrc(post);
  const images = post.imageCount ?? 0;
  const videos = post.videoCount ?? 0;
  const count = images + videos;
  const hidden = isHidden(post);
  const waiting = isWaiting(post);
  return (
    <div
      className={cn(
        'group relative aspect-[9/16] overflow-hidden rounded-lg bg-muted transition-opacity',
        selected && 'ring-2 ring-primary ring-offset-1',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="size-full text-left hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {src ? (
          <img
            src={src}
            alt=""
            className={cn('size-full object-cover', hidden && 'blur-[2px] grayscale')}
            loading="lazy"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <ImageIcon className="size-6" />
          </div>
        )}
        {post.createdAt && (
          <span className="absolute left-1 top-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
            {formatCardDate(post.createdAt)}
          </span>
        )}
        {hidden && (
          <span className="absolute right-1 top-1 rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
            Hidden
          </span>
        )}
        {!hidden && waiting && (
          <span className="absolute right-1 top-1 rounded bg-amber-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
            Waiting
          </span>
        )}
        {count > 0 && (
          <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white drop-shadow">
            {images > 0 && <ImageIcon className="size-4" />}
            {videos > 0 && <Video className="size-4" />}
            <span className="text-xs">{count}</span>
          </div>
        )}
      </button>
      {selectable && (
        <div
          className="absolute bottom-1 right-1 z-10"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect()}
            aria-label={selected ? 'Deselect post' : 'Select post'}
            className="border-white bg-black/40 data-[state=checked]:border-primary"
          />
        </div>
      )}
    </div>
  );
}

// User detail "All Content" media grid (op #6). 9:16 cards, paged, reset on userId.
// Cards navigate to the content detail route on click (ROUTES.contentDetails).
// Moderators can multi-select posts for bulk Take Down / Delete.
export function UserAllContent({
  userId,
  canModerate = false,
}: {
  userId: string;
  canModerate?: boolean;
}) {
  const navigate = useNavigate();
  const table = useUserTopContent(userId);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  // Drop selection when paging or switching users.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [userId, table.page]);

  const selectableIds = useMemo(
    () =>
      table.rows
        .filter((p) => p.id != null && !isTerminated(p))
        .map((p) => p.id as number),
    [table.rows],
  );

  const allPageSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  function toggleId(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const id of selectableIds) next.delete(id);
      } else {
        for (const id of selectableIds) next.add(id);
      }
      return next;
    });
  }

  const selectedList = useMemo(() => [...selectedIds], [selectedIds]);

  return (
    <Card id="all-content">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-sm">All Content</CardTitle>
        {canModerate && table.rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            {selectableIds.length > 0 && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={allPageSelected}
                  onCheckedChange={() => toggleSelectAllPage()}
                  aria-label="Select all on this page"
                />
                Select page
              </label>
            )}
            <BulkUserContentActions
              contentIds={selectedList}
              onClearSelection={() => setSelectedIds(new Set())}
            />
          </div>
        )}
      </CardHeader>
      <CardContent>
        {table.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : table.isError ? (
          <div className="space-y-2 py-6 text-center">
            <p className="text-sm text-destructive">Failed to load content.</p>
            <Button size="sm" variant="outline" onClick={() => void table.refetch()}>
              Retry
            </Button>
          </div>
        ) : table.rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No content.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
              {table.rows.map((p, i) => {
                const id = p.id;
                const selectable = canModerate && id != null && !isTerminated(p);
                return (
                  <AllContentCard
                    key={id ?? i}
                    post={p}
                    selectable={selectable}
                    selected={id != null && selectedIds.has(id)}
                    onToggleSelect={() => {
                      if (id != null) toggleId(id);
                    }}
                    onOpen={() => {
                      if (id != null) void navigate(ROUTES.contentDetails(String(id)));
                    }}
                  />
                );
              })}
            </div>
            <PaginationBar
              page={table.page}
              totalPage={table.totalPage}
              onPageChange={table.setPage}
              isFetching={table.isFetching}
              className="mt-2 border-t-0 pb-0"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
